import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User, Calendar, ArrowLeft, LogOut, Pencil, Save, X, MapPin, Camera, Shield, Flag, Bell, Goal, Trophy, Settings as SettingsIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import RadarChart from "@/components/RadarChart";
import ReportPlayerModal from "@/components/ReportPlayerModal";
import { FormeRecente, AideAmeliorations } from "@/components/ProfileSections";
import SettingsModal from "@/components/SettingsModal";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { subscribeReadySW, unsubscribeReadySW } from "@/services/push";
import { toast } from "sonner";

const STAT_LABELS = {
  vitesse: "Vitesse",
  tir: "Tir",
  passe: "Passe",
  dribble: "Dribble",
  defense: "Defense",
  physique: "Physique",
};

const STAT_KEYS = ["vitesse", "tir", "passe", "dribble", "defense", "physique"];

const DEFAULT_STATS = { vitesse: 50, tir: 50, passe: 50, dribble: 50, defense: 50, physique: 50 };

export default function ProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { search_radius_km } = useSettings();
  const radiusKm = search_radius_km ?? 15;
  const [player, setPlayer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editStats, setEditStats] = useState({});
  const [editFoot, setEditFoot] = useState("Droitier");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [badges, setBadges] = useState({ earned: [], total: 26 });
  const [showReportModal, setShowReportModal] = useState(false);
  const [notifyRadius, setNotifyRadius] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const fileInputRef = useRef(null);

  const isOwnProfile = !id;

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        let target = null;
        if (isOwnProfile) {
          setPlayer(user);
          target = user;
          setNotifyRadius(!!user?.push_active);
        } else {
          const data = await api.getUser(id);
          setPlayer(data);
          target = data;
        }
        if (target?.id) {
          try {
            const b = await api.getUserBadges(target.id);
            setBadges(b);
          } catch (_) {
            // badges optional
          }
        }
      } catch (error) {
        toast.error("Profil non trouve");
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };
    loadProfile();
  }, [id, user, isOwnProfile, navigate]);

  if (isLoading || !player) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid #333', borderTopColor: '#22c55e', borderRadius: '50%' }} />
      </div>
    );
  }

  const reliabilityScore = player.reliability || 100;
  const reliabilityColor = reliabilityScore >= 90 ? "#22c55e" : reliabilityScore >= 70 ? "#eab308" : "#ef4444";

  const handleBack = () => navigate(-1);
  const handleLogout = () => { logout(); navigate("/"); };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    return name.charAt(0).toUpperCase();
  };

  const handleEditStart = () => {
    setEditStats({ ...(player.stats || DEFAULT_STATS) });
    setEditFoot(player.dominant_foot || "Droitier");
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditStats({});
  };

  const handleStatChange = (key, value) => {
    const num = parseInt(value) || 10;
    setEditStats(prev => ({ ...prev, [key]: Math.min(99, Math.max(10, num)) }));
  };

  const handleSaveStats = async () => {
    try {
      setIsSaving(true);
      await api.updateUser(player.id, { stats: editStats, dominant_foot: editFoot });
      await refreshUser();
      setIsEditing(false);
      toast.success("Competences mises a jour!");
    } catch (error) {
      toast.error(error.message || "Erreur lors de la sauvegarde");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop lourde (max 5 Mo)");
      return;
    }
    try {
      await api.uploadPhoto(player.id, file);
      await refreshUser();
      toast.success("Photo mise a jour!");
    } catch (error) {
      toast.error(error.message || "Erreur upload");
    }
  };

  const photoUrl = player.photo_path ? api.getPhotoUrl(player.photo_path) : null;

  const currentStats = isEditing ? editStats : (player.stats || DEFAULT_STATS);

  return (
    <div data-testid="profile-page" style={{ minHeight: '100vh', backgroundColor: '#000' }}>
      {/* Header */}
      <header style={{ padding: '24px 16px 16px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {!isOwnProfile ? (
            <Button onClick={handleBack} variant="ghost" style={{ width: '36px', height: '36px', padding: 0, color: '#fff' }}>
              <ArrowLeft size={20} />
            </Button>
          ) : (
            <div style={{ width: '36px' }} />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={20} color="#22c55e" />
            <h1 className="font-sport uppercase" style={{ fontSize: '20px', fontWeight: 900, color: '#fff', letterSpacing: '0.02em' }}>
              {isOwnProfile ? "Mon Profil" : "Profil"}
            </h1>
          </div>
          {isOwnProfile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                data-testid="open-settings"
                onClick={() => setSettingsOpen(true)}
                variant="ghost"
                style={{ width: '36px', height: '36px', padding: 0, color: '#22c55e' }}
                aria-label="Parametres"
              >
                <SettingsIcon size={18} />
              </Button>
              <Button data-testid="logout-button" onClick={handleLogout} variant="ghost" style={{ width: '36px', height: '36px', padding: 0, color: '#6b7280' }}>
                <LogOut size={18} />
              </Button>
            </div>
          ) : (
            <div style={{ width: '36px' }} />
          )}
        </div>
      </header>

      {/* Admin shortcut */}
      {isOwnProfile && user?.role === "admin" && (
        <section style={{ padding: '0 16px 12px 16px' }}>
          <button
            data-testid="admin-dashboard-link"
            onClick={() => navigate("/admin-dashboard")}
            style={{
              width: '100%', minHeight: '46px', borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(234,179,8,0.15) 0%, rgba(234,179,8,0.06) 100%)',
              border: '1px solid rgba(234,179,8,0.4)',
              color: '#eab308', fontWeight: 700, fontSize: '13px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              cursor: 'pointer', touchAction: 'manipulation',
            }}
          >
            <Shield size={16} /> Admin Dashboard
          </button>
        </section>
      )}

      {/* Signaler ce joueur (only on others' profiles) */}
      {!isOwnProfile && player && (
        <section style={{ padding: '0 16px 12px 16px' }}>
          <button
            data-testid="report-player-btn"
            onClick={() => setShowReportModal(true)}
            style={{
              width: '100%', minHeight: '44px', borderRadius: '10px',
              backgroundColor: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.3)',
              color: '#f87171', fontWeight: 600, fontSize: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              cursor: 'pointer', touchAction: 'manipulation',
            }}
          >
            <Flag size={14} /> Signaler ce joueur
          </button>
        </section>
      )}

      {/* Alerte 15km toggle (own profile) */}
      {isOwnProfile && (
        <section data-testid="notify-radius-section" style={{ padding: '0 16px 12px 16px' }}>
          <div style={{
            backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
            borderRadius: '14px', padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '50%',
              backgroundColor: notifyRadius ? 'rgba(34,197,94,0.12)' : '#111',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: notifyRadius ? '1px solid rgba(34,197,94,0.4)' : '1px solid #2a2a2a',
            }}>
              <Bell size={16} color={notifyRadius ? '#22c55e' : '#6b7280'} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>Alerte {radiusKm} km</div>
              <div style={{ fontSize: '11px', color: '#9ca3af', lineHeight: 1.4 }}>
                Recois un push + email des qu'un match se cree pres de chez toi.
              </div>
            </div>
            <button
              data-testid="notify-radius-toggle"
              role="switch"
              aria-checked={notifyRadius}
              disabled={savingSettings}
              onClick={() => {
                // iOS REQUIREMENT: Notification.requestPermission() is invoked
                // SYNCHRONOUSLY as the VERY FIRST statement of the click — no await,
                // no condition, no variable before it.
                const permissionPromise = Notification.requestPermission();

                const next = !notifyRadius;
                setSavingSettings(true);
                permissionPromise
                  .then(async (permission) => {
                    try {
                      if (next) {
                        // Turn ON: stay GREEN only if the push subscription is REALLY created in DB.
                        if (permission !== "granted") {
                          toast.error(`Autorise les notifications pour activer l'alerte ${radiusKm} km`);
                          return;
                        }
                        const ok = await subscribeReadySW();
                        if (!ok) {
                          toast.error("Impossible d'activer les notifications");
                          return;
                        }
                        await api.updateMySettings({ notify_radius: true });
                        setNotifyRadius(true);
                        toast.success(`Alerte ${radiusKm} km activee`);
                      } else {
                        // Turn OFF: remove the subscription so the switch reflects the real state.
                        await unsubscribeReadySW();
                        await api.pushUnsubscribe().catch(() => {});
                        await api.updateMySettings({ notify_radius: false });
                        setNotifyRadius(false);
                        toast.success("Notifications desactivees");
                      }
                      refreshUser?.();
                    } catch (err) {
                      toast.error(err.message || "Echec MAJ");
                    } finally {
                      setSavingSettings(false);
                    }
                  })
                  .catch((err) => {
                    console.error("requestPermission failed", err);
                    setSavingSettings(false);
                  });
              }}
              style={{
                width: '46px', height: '26px', borderRadius: '999px',
                border: 'none', cursor: 'pointer', position: 'relative',
                backgroundColor: notifyRadius ? '#22c55e' : '#333',
                transition: 'background-color 0.2s ease',
                flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute', top: '3px',
                left: notifyRadius ? '23px' : '3px',
                width: '20px', height: '20px', borderRadius: '50%',
                backgroundColor: '#fff', transition: 'left 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </button>
          </div>
        </section>
      )}

      {showReportModal && player && (
        <ReportPlayerModal targetUser={player} onClose={() => setShowReportModal(false)} />
      )}

      {/* Profile Card */}
      <section style={{ padding: '0 16px 20px 16px' }}>
        <div style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '16px', padding: '24px', textAlign: 'center' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <Avatar style={{ width: '80px', height: '80px', margin: '0 auto 12px auto', border: '2px solid rgba(34,197,94,0.4)' }}>
              {photoUrl && <AvatarImage src={photoUrl} alt={player.name} />}
              <AvatarFallback style={{ backgroundColor: '#1f2937', color: '#22c55e', fontSize: '28px', fontWeight: 700 }}>
                {getInitials(player.name)}
              </AvatarFallback>
            </Avatar>
            {isOwnProfile && (
              <>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                <button
                  data-testid="upload-photo-button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    position: 'absolute', bottom: '10px', right: '-4px',
                    width: '28px', height: '28px', borderRadius: '50%',
                    backgroundColor: '#22c55e', border: '2px solid #111',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Camera size={12} color="#fff" />
                </button>
              </>
            )}
          </div>
          <h2 className="font-heading italic uppercase" style={{ fontSize: '18px', fontWeight: 900, color: '#fff', letterSpacing: '0.02em' }}>
            {player.name || "Utilisateur"}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
            {player.position && (
              <Badge data-testid="profile-position" style={{
                backgroundColor: player.position === 'Attaquant' ? 'rgba(239,68,68,0.15)' :
                  player.position === 'Milieu' ? 'rgba(234,179,8,0.15)' :
                  player.position === 'Defenseur' ? 'rgba(59,130,246,0.15)' : 'rgba(168,85,247,0.15)',
                color: player.position === 'Attaquant' ? '#f87171' :
                  player.position === 'Milieu' ? '#eab308' :
                  player.position === 'Defenseur' ? '#60a5fa' : '#a855f7',
                fontSize: '11px', padding: '2px 10px',
              }}>
                {player.position}
              </Badge>
            )}
            <Badge data-testid="profile-foot" style={{
              backgroundColor: 'rgba(34,197,94,0.15)',
              color: '#22c55e',
              fontSize: '11px', padding: '2px 10px',
            }}>
              🦶 {player.dominant_foot || 'Droitier'}
            </Badge>
            {player.city && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', fontSize: '12px' }}>
                <MapPin size={12} /> {player.city}
              </span>
            )}
          </div>
          <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '6px' }}>
            {player.matches_played || 0} matchs joues
          </p>
          {(player.mvp_count > 0 || player.goals >= 10) && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
              {player.mvp_count > 0 && (
                <Badge style={{ backgroundColor: 'rgba(234,179,8,0.15)', color: '#eab308', fontSize: '10px', padding: '2px 8px' }}>
                  🥇 MVP x{player.mvp_count}
                </Badge>
              )}
              {player.goals >= 10 && (
                <Badge style={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e', fontSize: '10px', padding: '2px 8px' }}>
                  ⚽ Buteur
                </Badge>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Stats Grid */}
      <section data-testid="profile-stats-grid" style={{ padding: '0 16px 20px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          <div data-testid="stat-matches" style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '12px', padding: '14px 4px', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '24px' }}>
              <Calendar size={16} color="#22c55e" />
            </div>
            <div className="font-sport" style={{ fontSize: '26px', fontWeight: 700, color: '#fff', marginTop: '6px', fontVariantNumeric: 'tabular-nums' }}>{player.matches_played || 0}</div>
            <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Matchs</div>
          </div>
          <div data-testid="stat-goals" style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '12px', padding: '14px 4px', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '24px' }}>
              <Goal size={16} color="#22c55e" />
            </div>
            <div className="font-sport" style={{ fontSize: '26px', fontWeight: 700, color: '#fff', marginTop: '6px', fontVariantNumeric: 'tabular-nums' }}>{player.goals || 0}</div>
            <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Buts</div>
          </div>
          <div data-testid="stat-mvp" style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '12px', padding: '14px 4px', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '24px' }}>
              <Trophy size={16} color="#22c55e" />
            </div>
            <div className="font-sport" style={{ fontSize: '26px', fontWeight: 700, color: '#fff', marginTop: '6px', fontVariantNumeric: 'tabular-nums' }}>{player.mvp_count || 0}</div>
            <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>MVP</div>
          </div>
        </div>
      </section>

      {/* Reliability */}
      <section style={{ padding: '0 16px 20px 16px' }}>
        <div style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '16px', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 className="font-heading italic uppercase" style={{ fontSize: '13px', fontWeight: 900, color: '#fff', letterSpacing: '0.04em' }}>Fiabilite</h3>
            <span style={{ fontSize: '18px', fontWeight: 700, color: reliabilityColor }}>{reliabilityScore}%</span>
          </div>
          <Progress value={reliabilityScore} className="h-2" style={{ backgroundColor: '#222' }} indicatorClassName={reliabilityScore >= 90 ? "bg-[#22c55e]" : reliabilityScore >= 70 ? "bg-yellow-500" : "bg-red-500"} />
        </div>
      </section>

      {/* Radar Chart + Skills */}
      <section style={{ padding: '0 16px 20px 16px' }}>
        <div style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '16px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h3 className="font-heading italic uppercase" style={{ fontSize: '13px', fontWeight: 900, color: '#fff', letterSpacing: '0.04em' }}>
              Competences FIFA
            </h3>
            {isOwnProfile && !isEditing && (
              <button
                data-testid="edit-skills-button"
                onClick={handleEditStart}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '6px 12px', borderRadius: '8px',
                  backgroundColor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                  color: '#22c55e', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                }}
              >
                <Pencil size={12} /> Modifier
              </button>
            )}
            {isEditing && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  data-testid="cancel-skills-button"
                  onClick={handleEditCancel}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 10px', borderRadius: '8px',
                    backgroundColor: 'transparent', border: '1px solid #333',
                    color: '#9ca3af', fontSize: '12px', cursor: 'pointer',
                  }}
                >
                  <X size={12} /> Annuler
                </button>
                <button
                  data-testid="save-skills-button"
                  onClick={handleSaveStats}
                  disabled={isSaving}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 12px', borderRadius: '8px',
                    backgroundColor: '#22c55e', border: 'none',
                    color: '#fff', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                    opacity: isSaving ? 0.5 : 1,
                  }}
                >
                  <Save size={12} /> {isSaving ? "..." : "Sauver"}
                </button>
              </div>
            )}
          </div>

          <RadarChart stats={currentStats} size={160} />

          {/* Edit Sliders */}
          {isEditing && (
            <div data-testid="skills-editor" style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '11px', color: '#6b7280', textAlign: 'center', marginBottom: '16px', fontStyle: 'italic' }}>
                Note-toi honnetement de 10 a 99
              </p>
              <div data-testid="foot-selector" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, marginBottom: 16, padding: '10px 12px',
                background: '#0a0a0a', border: '1px solid #27272a', borderRadius: 10,
              }}>
                <span style={{ fontSize: 13, color: '#d1d5db', fontWeight: 500 }}>
                  🦶 Pied fort
                </span>
                <select
                  data-testid="foot-select"
                  value={editFoot}
                  onChange={(e) => setEditFoot(e.target.value)}
                  style={{
                    background: '#111', color: '#fff', border: '1px solid #333',
                    borderRadius: 8, padding: '6px 10px', fontSize: 13, cursor: 'pointer',
                  }}
                >
                  <option value="Droitier">Droitier</option>
                  <option value="Gaucher">Gaucher</option>
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {STAT_KEYS.map((key) => (
                  <div key={key} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 44px', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#d1d5db', fontWeight: 500 }}>
                      {STAT_LABELS[key]}
                    </span>
                    <input
                      data-testid={`slider-${key}`}
                      type="range"
                      min="10"
                      max="99"
                      value={editStats[key] || 50}
                      onChange={(e) => handleStatChange(key, e.target.value)}
                      style={{
                        width: '100%', height: '6px', borderRadius: '3px',
                        appearance: 'none', WebkitAppearance: 'none',
                        background: `linear-gradient(to right, #22c55e ${((editStats[key] || 50) - 10) / 89 * 100}%, #333 ${((editStats[key] || 50) - 10) / 89 * 100}%)`,
                        outline: 'none', cursor: 'pointer',
                      }}
                    />
                    <span style={{
                      fontSize: '14px', fontWeight: 700, color: '#22c55e',
                      textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                    }}>
                      {editStats[key] || 50}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Forme + Aide */}
      <FormeRecente userId={player.id} />

      {/* Badges */}
      <section data-testid="badges-section" style={{ padding: '0 16px 20px 16px' }}>
        <div style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '16px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 className="font-heading italic uppercase" style={{ fontSize: '13px', fontWeight: 900, color: '#fff', letterSpacing: '0.04em' }}>Badges</h3>
            <span style={{ fontSize: '11px', color: '#22c55e', fontWeight: 600 }}>
              {badges.earned.length}/{badges.total}
            </span>
          </div>
          {badges.earned.length === 0 ? (
            <p style={{ fontSize: '11px', color: '#6b7280', textAlign: 'center', padding: '12px' }}>
              Aucun badge gagne pour le moment. Joue, marque, gagne le MVP !
            </p>
          ) : (
            <div data-testid="badges-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
              {badges.earned.slice(0, 8).map(b => (
                <div
                  key={b.code}
                  data-testid={`badge-${b.code}`}
                  title={`${b.label} — ${b.desc}`}
                  style={{
                    aspectRatio: '1 / 1',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(34,197,94,0.08)',
                    border: '1px solid rgba(34,197,94,0.3)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    padding: '6px', textAlign: 'center',
                  }}
                >
                  <span style={{ fontSize: '18px', lineHeight: 1, marginBottom: '4px' }}>🏅</span>
                  <span style={{ fontSize: '9px', fontWeight: 600, color: '#22c55e', lineHeight: 1.1 }}>
                    {b.label}
                  </span>
                </div>
              ))}
            </div>
          )}
          {isOwnProfile && (
            <button
              data-testid="see-all-badges-btn"
              onClick={() => navigate("/badges")}
              style={{
                marginTop: '12px', width: '100%', minHeight: '44px',
                borderRadius: '10px', backgroundColor: 'transparent',
                border: '1px solid #333', color: '#22c55e',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer', touchAction: 'manipulation',
              }}
            >
              Voir tous les 26 badges
            </button>
          )}
        </div>
      </section>

      {/* Rating */}
      <section style={{ padding: '0 16px 20px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: player.peer_rating != null ? '1fr 1fr' : '1fr', gap: '10px' }}>
          <div style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.1) 0%, transparent 100%)', border: '0.5px solid rgba(34,197,94,0.3)', borderRadius: '14px', padding: '12px 14px', textAlign: 'center' }}>
            <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Note Globale</p>
            <p className="font-heading" style={{ fontSize: '26px', fontWeight: 800, color: '#22c55e', lineHeight: 1.1, marginTop: 2 }}>{player.rating?.toFixed(1) || "5.0"}</p>
            <p data-testid="next-note-update" style={{ fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>
              MAJ Dimanche 23h
            </p>
          </div>
          {player.peer_rating != null && (
            <div data-testid="peer-rating" style={{ background: 'linear-gradient(135deg, rgba(234,179,8,0.1) 0%, transparent 100%)', border: '0.5px solid rgba(234,179,8,0.3)', borderRadius: '14px', padding: '12px 14px', textAlign: 'center' }}>
              <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Evaluation</p>
              <p className="font-heading" style={{ fontSize: '26px', fontWeight: 800, color: '#eab308', lineHeight: 1.1, marginTop: 2 }}>{player.peer_rating.toFixed(1)}<span style={{ fontSize: '13px', color: '#6b7280' }}>/10</span></p>
            </div>
          )}
        </div>
      </section>

      <AideAmeliorations />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
